import type { ComputedRef } from 'vue'
import { useAuthStore } from '@/features/auth'
import type { MaterialAuth } from '@/material/web/api'
import { MaterialAuthExpiredError } from '@/material/web/api'

function isValidMaterialAuth(auth: MaterialAuth | null): auth is MaterialAuth {
  return !!auth && auth.uid > 0 && !!auth.sessionId
}

/**
 * 对素材库接口统一做“会话失效 -> 刷新全局 session -> 重试一次”的处理。
 */
export async function runWithMaterialSessionRetry<T>(
  authRef: ComputedRef<MaterialAuth | null>,
  context: string,
  task: (auth: MaterialAuth) => Promise<T>,
): Promise<T> {
  const authStore = useAuthStore()
  const auth = authRef.value
  if (!isValidMaterialAuth(auth)) {
    throw new Error('请先登录后再使用素材库')
  }

  try {
    return await task(auth)
  } catch (error) {
    if (!(error instanceof MaterialAuthExpiredError)) {
      throw error
    }

    const refreshed = await authStore.refreshSessionForRetry()
    const latestAuth = {
      uid: authStore.uid || auth.uid,
      sessionId: authStore.sessionId,
    }
    if (refreshed && isValidMaterialAuth(latestAuth)) {
      try {
        return await task(latestAuth)
      } catch (retryError) {
        if (!(retryError instanceof MaterialAuthExpiredError)) {
          throw retryError
        }
      }
    }
    throw new Error('素材库登录态已失效，请重新登录')
  }
}
